/** Browser capability checks (docs/PRD.md US-2.3: notifications, iOS home-screen install). */

/** iPhone / iPad, including iPadOS that reports itself as a Mac. */
export const isIos = () =>
  /iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)

/** Opened from the home screen (installed PWA), not in a browser tab. */
export const isStandalone = () =>
  (typeof matchMedia === 'function' && matchMedia('(display-mode: standalone)').matches) ||
  (navigator as Navigator & { standalone?: boolean }).standalone === true

type InstallPromptEvent = Event & { prompt(): Promise<void>; userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }> }
let deferredInstall: InstallPromptEvent | null = null

/**
 * Chrome/Android/desktop: keep the browser's install prompt for our own "Simpan ke layar utama" button (Pengaturan).
 * Call once at startup; the event fires early and only once per page load.
 */
export function captureInstallPrompt() {
  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault()
    deferredInstall = e as InstallPromptEvent
  })
  window.addEventListener('appinstalled', () => {
    deferredInstall = null
  })
}
export const canPromptInstall = () => deferredInstall !== null
/** Shows the browser's own install dialog (once per captured event). */
export async function promptInstall(): Promise<'accepted' | 'dismissed' | 'unavailable'> {
  const e = deferredInstall
  if (!e) return 'unavailable'
  deferredInstall = null
  await e.prompt()
  return (await e.userChoice).outcome
}

/** Current notification permission, or 'unsupported' (old browsers, iOS outside the home screen). */
export const notificationState = (): NotificationPermission | 'unsupported' =>
  typeof Notification === 'undefined' ? 'unsupported' : Notification.permission

/** iOS only delivers web notifications to an app saved to the home screen. */
export const needsHomeScreenForNotifications = () => isIos() && !isStandalone()

/**
 * Ask for notification permission once, from a tap. Never throws and never blocks the caller's flow: a "no", an
 * unsupported browser or an earlier decision all resolve quietly.
 */
export async function askNotificationPermission(): Promise<NotificationPermission | 'unsupported'> {
  if (typeof Notification === 'undefined') return 'unsupported'
  if (Notification.permission !== 'default') return Notification.permission
  try {
    return await Notification.requestPermission()
  } catch {
    return 'default'
  }
}
